import React, { useEffect, useState } from 'react';
import Tabs, { TabPane } from 'rc-tabs';
import { useTranslation } from 'react-i18next';

import { DateTime } from 'luxon';
import { renderToString } from 'react-dom/server';
import { Spinner } from 'reactstrap';

import UnifiedInboxActivityTile from './unified-inbox-activity-tile';

import ActivityComponent from '../../../shared/design-system/components/molecules/activity-component';
import Tab from '../../../shared/design-system/components/atoms/tab';
import EmptyDataIcon1 from '../../../shared/components/images/empty-data-icon-1';
import Modal from '../../../shared/design-system/components/atoms/modal';
import ImageIcon from '../../../shared/components/images/image-icon';

import MasterLog from '../../../shared/enums/master-log-enum';
import { executeOnRequestStatus } from '../../../shared/utils/execute-on-request-status';
import { showGeneralErrorNotification } from '../../../shared/utils/errors';
import TruncatedTextWithTooltip from '../../../shared/components/truncated-text-with-tooltip';
import { getActivityWithLabel } from '../../mailbox-emails/helpers/helper';
import {
  getUnifiedInboxActivityIcon,
  getUnifiedInboxActivityLabel,
} from '../components/unified-inbox-mails-content/helper';

const unifiedInboxActivityTabs = [
  MasterLog.UniboxEmailOpened,
  MasterLog.UniboxEmailLinkClicked,
];

const UnifiedInboxEmailsActivityModal = ({
  show,
  tab,
  onClose,
  userTimezone,
  subject,
  emailId,
  email,
  unifiedInboxEmailThreadActivity,
  sendGetEmailThreadActivity,
  resetUnifiedInboxEmailThreadActivity,
  getEmailThreadActivityRequestStatus,
  getEmailThreadActivityRequestError,
  className = '',
}) => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<MasterLog>(tab);
  const [loading, setLoading] = useState<boolean>(true);

  const onTabChange = (key: string) => {
    if (key === MasterLog.UniboxEmailOpened) {
      setActiveTab(MasterLog.UniboxEmailOpened);
    }
    if (key === MasterLog.UniboxEmailLinkClicked) {
      setActiveTab(MasterLog.UniboxEmailLinkClicked);
    }
  };

  const handleActivityItemsFilters = (tabKey, item): boolean =>
    item.key === tabKey;

  const generatedTabs = unifiedInboxActivityTabs.map(
    (unifiedInboxActivityTab) => {
      // Creating an activity arr
      let activityArr = [];

      // Set to track the dayText that we already have got. like "20 July 2020"
      const dayTextSet = new Set();

      unifiedInboxEmailThreadActivity
        ?.filter((item) =>
          handleActivityItemsFilters(unifiedInboxActivityTab, item),
        )
        .forEach(({ timestamp, log, key }) => {
          // Generate Log HTML
          const logHtml = renderToString(
            <UnifiedInboxActivityTile
              timestamp={timestamp}
              log={log}
              type={key}
              timezone={userTimezone}
              className={className}
            />,
          );

          const userTimestamp = DateTime.fromISO(timestamp, {
            zone: userTimezone,
          })
            .toFormat('dd LLLL yyyy')
            .toString();

          if (!dayTextSet.has(userTimestamp)) {
            const activityArrElement = {
              dayText: userTimestamp,
              activity: [{ log: logHtml, key }],
            };
            dayTextSet.add(userTimestamp);
            activityArr.push(activityArrElement);
          } else {
            const activityArrElement = activityArr.find(
              ({ dayText }) => dayText === userTimestamp,
            );
            activityArrElement.activity.push({ log: logHtml, key });
          }
        });

      activityArr = getActivityWithLabel(activityArr, t);

      const activityCount = activityArr.reduce(
        (a, c) => a + c.activity.length,
        0,
      );

      return (
        <TabPane
          key={unifiedInboxActivityTab}
          className={unifiedInboxActivityTab}
          tab={
            <Tab
              label={t(
                `labels.${getUnifiedInboxActivityLabel(
                  unifiedInboxActivityTab,
                )}`,
              )}
              icon={
                <ImageIcon
                  src={getUnifiedInboxActivityIcon(
                    unifiedInboxActivityTab,
                    activeTab === unifiedInboxActivityTab,
                  )}
                />
              }
              counter={activityCount}
            />
          }
        >
          {loading && activeTab === tab && (
            <div
              className={`w-100 h-100 text-center ${
                className !== ''
                  ? 'd-flex justify-content-center align-items-center'
                  : 'mt-5'
              }`}
            >
              <Spinner className="spinner--blue" animation="border" />
            </div>
          )}

          {!loading &&
            activityArr.length > 0 &&
            activityArr.map(({ id, dayText, activity }) => (
              <ActivityComponent
                key={id}
                dayText={dayText}
                activity={activity}
              />
            ))}

          {!loading && activityArr.length === 0 && (
            <div className="mailbox-empty-block d-flex flex-column justify-content-center align-items-center mt-5 mx-auto">
              <div className="empty-icon-wrapper">
                <EmptyDataIcon1 />
              </div>
              <div className="empty-message-wrapper text-center">
                <h2 className="empty-message-heading text-center font-bold font-14 blue-txt-15">
                  {t('labels.no_data_available')}
                </h2>
                <div className="empty-message">
                  <p className="description text-center font-12 gray-txt-15">
                    {t('messages.no_data_available')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </TabPane>
      );
    },
  );

  useEffect(() => {
    if (show) {
      sendGetEmailThreadActivity({
        id: emailId,
        type: [MasterLog.UniboxEmailOpened, MasterLog.UniboxEmailLinkClicked],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    executeOnRequestStatus({
      status: getEmailThreadActivityRequestStatus,
      onSuccess: () => {
        setLoading(false);
      },
      onFailed: () => {
        if (getEmailThreadActivityRequestError) {
          showGeneralErrorNotification(
            getEmailThreadActivityRequestError.message,
          );
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getEmailThreadActivityRequestStatus]);

  useEffect(
    () => () => {
      resetUnifiedInboxEmailThreadActivity();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Modal
      show={show}
      className={`activity-modal ${className}`}
      titleContent={
        <div className="pt-3 pb-3">
          <TruncatedTextWithTooltip
            string={subject}
            length={
              className === 'prospect-kanban-email-thread-activity-modal'
                ? 100
                : 90
            }
            tooltipClassName="tooltip-xl"
            textClassName="d-block font-16 font-semibold blue-txt-15 white-space-nowrap"
          />
          <span className="d-block font-14 font-medium gray-txt-15">
            {email}
          </span>
        </div>
      }
      onClose={onClose}
      showCloseIcon={true}
      hideFooter={true}
      backdrop="static"
      extraModalProps={{
        centered: true,
        'aria-labelledby': 'contained-modal-title-vcenter',
      }}
    >
      <Tabs
        activeKey={activeTab}
        defaultActiveKey={MasterLog.UniboxEmailOpened}
        prefixCls="bs-tabs"
        className="bs-tabs-small activity-tabs"
        onChange={onTabChange}
      >
        {generatedTabs}
      </Tabs>
    </Modal>
  );
};

export default UnifiedInboxEmailsActivityModal;
